import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "store/index";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

import InnerInput from "components/inputs/InnerInput/InnerInput";
import Button from "components/buttons/Button";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import Tooltip from "components/Tooltip";
import TextEditor from "components/wysiwyg/Wysiwyg";

interface Props {
  id?: string;
  type: string;
  handleData: (e: any) => void;
  currentData: any;
  onInitialData?: (data: any) => void;
  isReadOnly?: boolean;
  onLoadingChange?: (isLoading: boolean) => void;
  fieldErrors?: Record<string, string>;
  clearFieldError?: (field: string) => void;
}

const SeoTab: React.FC<Props> = ({
  id,
  type,
  handleData,
  currentData,
  onInitialData,
  isReadOnly = true,
  onLoadingChange,
  fieldErrors,
  clearFieldError,
}) => {
  const { t } = useTranslation();
  const canBeUpdated = !isReadOnly;

  const localesList = useAppSelector((store) => store.settings.locales);

  const [isFetched, setIsFetched] = useState<boolean>(false);
  const [isAIFetching, setIsAIFetching] = useState<boolean>(false);
  const [prevData, setPrevData] = useState<any>();
  const [activeLocale, setActiveLocale] = useState<string>();

  useEffect(() => {
    const firstLocale = localesList?.[0];
    if (firstLocale) {
      setActiveLocale(firstLocale);
    }
  }, [localesList]);

  useEffect(() => {
    onLoadingChange?.(!isFetched);
  }, [isFetched, onLoadingChange]);

  const handleGet = async () => {
    if (!id || !activeLocale) return;

    setIsFetched(false);
    try {
      const result = await instance.get(
        `${apiRoutes.getSeoAtrs}/${type}/${id}/${activeLocale}`,
      );
      handleData({ ...result?.data, locale: activeLocale });
      setPrevData({ ...result?.data, locale: activeLocale });
      onInitialData?.({ ...result?.data, locale: activeLocale });
    } catch {
      console.log("handleGet error");
    }
    setIsFetched(true);
  };

  useEffect(() => {
    handleGet();
  }, [activeLocale, id]);

  const handleAIText = async (attribute: string) => {
    if (!canBeUpdated) return;

    setIsAIFetching(true);
    try {
      const result = await instance.post(apiRoutes.generateAIText, {
        instanceType: type,
        attribute: attribute,
        instanceId: id,
        locale: activeLocale,
      });
      if (!prevData) {
        setPrevData(currentData);
      }
      handleData({ ...currentData, [attribute]: result.data.text });
    } catch {
      console.log("handleAIText error");
    }
    setIsAIFetching(false);
  };

  return (
    <div className="game-form-seo">
      <div className="game-modal-tabs small">
        {localesList.map((el) => (
          <div
            className={`tab-item ${activeLocale === el ? "active" : ""}`}
            key={el}
            onClick={() => {
              clearFieldError?.("fullDescription");
              setActiveLocale(el);
              handleData({
                ...currentData,
                locale: el,
              });
            }}
          >
            <img
              src={`https://flagpedia.net/data/flags/icon/16x12/${el.split("-")[1]?.toLowerCase()}.png`}
              alt=""
            />
            {t(el).replace(/-[a-z]+$/i, "")}
          </div>
        ))}
      </div>
      {isFetched ? (
        <>
          <div className="game-form-content">
            <div className="game-form-box">
              <div className="game-form-box--item with-ai">
                <div className="ai-buttons">
                  <Button
                    handleClick={() => {
                      handleData({
                        ...currentData,
                        headTitle: prevData.headTitle ?? "",
                      });
                    }}
                    disabled={!canBeUpdated || !prevData || isAIFetching}
                    isSecondary
                    txt="&larr;"
                  />
                  <Button
                    disabled={!canBeUpdated || isAIFetching}
                    handleClick={() => {
                      handleAIText("headTitle");
                    }}
                    txt={t("aiAssist")}
                  />
                </div>
                <InnerInput
                  value={currentData.headTitle}
                  placeholder={t("headTitle")}
                  label={t("headTitle")}
                  tooltipText={t("headTitleTooltip")}
                  tooltipPosition="top-left"
                  type="text"
                  id="game-headTitle"
                  onChange={(e) => handleData({ ...currentData, headTitle: e })}
                  disabled={!canBeUpdated}
                />
              </div>
              <div className="game-form-box--item with-ai">
                <div className="ai-buttons">
                  <Button
                    handleClick={() => {
                      handleData({
                        ...currentData,
                        twitterTitle: prevData.twitterTitle ?? "",
                      });
                    }}
                    disabled={!canBeUpdated || !prevData || isAIFetching}
                    isSecondary
                    txt="&larr;"
                  />
                  <Button
                    disabled={!canBeUpdated || isAIFetching}
                    handleClick={() => {
                      handleAIText("twTitle");
                    }}
                    txt={t("aiAssist")}
                  />
                </div>
                <InnerInput
                  value={currentData.twitterTitle}
                  placeholder={t("twTitle")}
                  label={t("twTitle")}
                  tooltipText={t("twTitleTooltip")}
                  tooltipPosition="top-left"
                  type="text"
                  id="game-twTitle"
                  onChange={(e) =>
                    handleData({ ...currentData, twitterTitle: e })
                  }
                  disabled={!canBeUpdated}
                />
              </div>
              <div className="game-form-box--item with-ai">
                <div className="ai-buttons">
                  <Button
                    handleClick={() => {
                      handleData({
                        ...currentData,
                        ogTitle: prevData.ogTitle ?? "",
                      });
                    }}
                    disabled={!canBeUpdated || !prevData || isAIFetching}
                    isSecondary
                    txt="&larr;"
                  />
                  <Button
                    disabled={!canBeUpdated || isAIFetching}
                    handleClick={() => {
                      handleAIText("ogTitle");
                    }}
                    txt={t("aiAssist")}
                  />
                </div>
                <InnerInput
                  value={currentData.ogTitle}
                  placeholder={t("ogTitle")}
                  label={t("ogTitle")}
                  tooltipText={t("ogTitleTooltip")}
                  tooltipPosition="top-left"
                  type="text"
                  id="game-ogTitle"
                  onChange={(e) => handleData({ ...currentData, ogTitle: e })}
                  disabled={!canBeUpdated}
                />
              </div>
            </div>
            <div className="game-form-box">
              <div className="game-form-box--item with-ai">
                <div className="ai-buttons">
                  <Button
                    handleClick={() => {
                      handleData({
                        ...currentData,
                        metaDescription: prevData.metaDescription ?? "",
                      });
                    }}
                    disabled={!canBeUpdated || !prevData || isAIFetching}
                    isSecondary
                    txt="&larr;"
                  />
                  <Button
                    disabled={!canBeUpdated || isAIFetching}
                    handleClick={() => {
                      handleAIText("metaDescription");
                    }}
                    txt={t("aiAssist")}
                  />
                </div>
                <InnerInput
                  value={currentData.metaDescription}
                  placeholder={t("metaDescription")}
                  label={t("metaDescription")}
                  tooltipText={t("metaDescriptionTooltip")}
                  tooltipPosition="top-left"
                  type="textarea"
                  id="game-metaDescription"
                  disabled={!canBeUpdated}
                  onChange={(e) =>
                    handleData({ ...currentData, metaDescription: e })
                  }
                  rows={1}
                />
              </div>
              <div className="game-form-box--item with-ai">
                <div className="ai-buttons">
                  <Button
                    handleClick={() => {
                      handleData({
                        ...currentData,
                        twitterDescription: prevData.twitterDescription ?? "",
                      });
                    }}
                    disabled={!canBeUpdated || !prevData || isAIFetching}
                    isSecondary
                    txt="&larr;"
                  />
                  <Button
                    disabled={!canBeUpdated || isAIFetching}
                    handleClick={() => {
                      handleAIText("twitterDescription");
                    }}
                    txt={t("aiAssist")}
                  />
                </div>
                <InnerInput
                  value={currentData.twitterDescription}
                  placeholder={t("twitterDescription")}
                  label={t("twitterDescription")}
                  tooltipText={t("twitterDescriptionTooltip")}
                  tooltipPosition="top-left"
                  type="textarea"
                  id="game-twDescription"
                  onChange={(e) =>
                    handleData({ ...currentData, twitterDescription: e })
                  }
                  rows={1}
                  disabled={!canBeUpdated}
                />
              </div>
              <div className="game-form-box--item with-ai">
                <div className="ai-buttons">
                  <Button
                    handleClick={() => {
                      handleData({
                        ...currentData,
                        ogDescription: prevData.ogDescription ?? "",
                      });
                    }}
                    disabled={!canBeUpdated || !prevData || isAIFetching}
                    isSecondary
                    txt="&larr;"
                  />
                  <Button
                    disabled={!canBeUpdated || isAIFetching}
                    handleClick={() => {
                      handleAIText("ogDescription");
                    }}
                    txt={t("aiAssist")}
                  />
                </div>
                <InnerInput
                  value={currentData.ogDescription}
                  placeholder={t("ogDescription")}
                  label={t("ogDescription")}
                  tooltipText={t("ogDescriptionTooltip")}
                  tooltipPosition="top-left"
                  type="textarea"
                  id="game-ogDescription"
                  disabled={!canBeUpdated}
                  onChange={(e) =>
                    handleData({ ...currentData, ogDescription: e })
                  }
                  rows={1}
                />
              </div>
            </div>
          </div>
          <div className="game-form-full-description">
            <div className="game-form-box--item html-editor">
              <div className="label">
                {t("fullDescription")}
                <Tooltip text={t("fullDescriptionTooltip")} position="top-left">
                  <button className="info-icon">ℹ</button>
                </Tooltip>
              </div>
              <TextEditor
                value={currentData.fullDescription || ""}
                hasError={Boolean(fieldErrors?.fullDescription)}
                onChange={(value) => {
                  handleData({ ...currentData, fullDescription: value });
                  clearFieldError?.("fullDescription");
                }}
                disabled={!canBeUpdated}
              />
              {fieldErrors?.fullDescription && (
                <div
                  role="alert"
                  style={{ color: "var(--error-color)", marginTop: 8 }}
                >
                  {fieldErrors.fullDescription === "fullDescriptionIsString"
                    ? "Full description must be a string."
                    : fieldErrors.fullDescription}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <ModalTabSkeleton />
      )}
    </div>
  );
};

export default SeoTab;
